import {Component, h} from "preact";
import {connect} from "redux-zero/preact";
import {withDeviceIndicator} from "../../hoc/withDeviceIndicator";
import actions from "../../store/actions";
import App from "../../components/app";
import DetailDesktop from "../../components/detail/desktop";
import DetailMobile from "../../components/detail/mobile";
import {productService} from "../../services/product-service";
import {Product, Variant} from "../../models/product";
import {customerService} from "../../services/customer-service";
import {Like} from "../../models/customer";
import Interstitial from "../../components/interstitial";
import {NY_Tracker} from "../../services/tracking-service";
import {i18nService} from "../../services/i18n-service";
import {imageService} from "../../services/image-service";
import {updateMetadata} from "../../hoc/metadata";
import {config} from "../../config";
import {WithContext, Product as ProductSchema} from "schema-dts";

interface Props {
  setLikes(likes: Like[]);

  isInterstitialOpen: boolean;

  productId?: string;
  variantId?: string;
  isMobile: boolean;

  clearAllFilters();
}

const mapToProps = ({isInterstitialOpen}) => ({isInterstitialOpen});

interface State {
  product: Product;
  variant: Variant;
  notAvailable: boolean;
  title: string;
  description: string;
}

export class Detail extends Component<Props, State> {

  componentWillMount() {
    this.setState({
      title: document.title,
      description: document.querySelector("meta[name='description']")?.getAttribute("content")
    })
  }

  async componentDidMount(): Promise<void> {
    window.scrollTo(0, 0);
    this.setState({notAvailable: true});
    await this.loadProduct(this.props.productId, this.props.variantId);
    NY_Tracker.trackPage(document.location.pathname, document.title);
    customerService.subscribeForCustomer(async (data) => {
      if (!customerService.isLoggedIn) {
        this.props.setLikes([]);
      }
      if (data) {
        const {userLikes} = await customerService.loadFavouriteProducts(App.countryCode);
        this.props.setLikes(userLikes).then(() => customerService.likeCachedVariant());
      }
    });
  }

  componentWillUnmount() {
    updateMetadata({
      title: this.state.title,
      description: this.state.description
    })

    document.querySelector("script[type='application/ld+json']")?.remove();
  }

  async componentWillReceiveProps(nextProps: Props) {
    if (nextProps.productId != this.props.productId || nextProps.variantId != this.props.variantId) {
      await this.loadProduct(nextProps.productId, nextProps.variantId);
      NY_Tracker.trackPage(document.location.pathname, document.title);
    }
  }


  private async loadProduct(productId: string, variantId: string) {
    let product = await productService.loadProductById(productId, App.countryCode);
    let variant: Variant | null = null;

    if (product) {
      for (const pv of product.variants) {
        if (pv.productId === productId && pv.id === variantId) {
          variant = pv;
          break;
        }
      }
      this.setState({product: product, variant: variant});

      if (variant) {
        const metadataText = `${i18nService.translate(productService.getTranslationKey(product))} | ${variant.getColorTranslation()} | NEW YORKER`;

        updateMetadata({
          title: metadataText,
          description: metadataText,
          canonical: window.location.href,
          image: imageService.getUrl(variant?.images.find(e => e.type === 'CUTOUT'), 'low'),
          imageAlt: i18nService.translate(productService.getTranslationKey(product))
        });

        this.loadStructuredData(product, variant);
      }
    }
    this.setState({notAvailable: false});
  }

  loadStructuredData(product: Product, variant: Variant) {
    document.querySelector("script[type='application/ld+json']")?.remove();

    if (!product || !variant || !variant.currentPrice) return

    let image = variant.images.find(e => e.type === 'OUTFIT_IMAGE')
    if (!image) {
      image = variant.images.find(e => e.type === 'CUTOUT')
    }

    const script = document.createElement("script")
    script.setAttribute("type", "application/ld+json")

    script.innerText = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Product",
      "itemCondition": "https://schema.org/NewCondition",
      "image": image ? imageService.getUrl(image, 'high') : "",
      "name": i18nService.translate(productService.getTranslationKey(product)),
      "description": `${i18nService.translate(productService.getTranslationKey(product))} | ${variant.getColorTranslation()} | NEW YORKER`,
      "color": variant.getColorTranslation(),
      "sku": variant.productId,
      "brand": {
        "@type": "Brand",
        "name": product.brand
      },
      "offers": {
        "@type": "Offer",
        "url": `https://www.newyorker.de${config.url(`/detail/${variant.productId}/${variant.id}`)}`,
        "priceCurrency": variant.currency,
        "price": variant.currentPrice,
        "seller": {
          "@type": "Organization",
          "name": "NEW YORKER"
        }
      }
    } as WithContext<ProductSchema>)

    document.head.appendChild(script)
  }

  render() {
    if (this.state.notAvailable) {
      return null;
    }

    return (
      <div>
        {this.props.isInterstitialOpen ? <Interstitial/> : null}
        {this.props.isMobile && <DetailMobile product={this.state.product} variant={this.state.variant}/>}
        {!this.props.isMobile && <DetailDesktop product={this.state.product} variant={this.state.variant}/>}
      </div>
    )
  }
}

export default connect(mapToProps, actions)(withDeviceIndicator(Detail));
